import { useCallback, useState } from 'react'
import { AfRouterAdapter, CetusAggregatorAdapter } from '@kunalabs-io/kai'

import { SwapRouter } from 'contexts/SwapRouterContext'

const INITIAL_SLIPPAGE = 0.05
const INITIAL_OVERESTIMATION = 0.05
const INITIAL_SWAP_TYPE = 'exact-in'

export const WITHDRAW_DIALOG_ID = 'withdraw-dialog'

export type SwapType = 'exact-in' | 'exact-out'

const AF_PROTOCOL_LIST_LS_KEY = 'af-protocols'
const CETUS_PROTOCOL_LIST_LS_KEY = 'cetus-protocols'

export const getProtocolDenyList = (
  routerLabel: SwapRouter,
  afProtocols: Record<string, boolean>,
  cetusProtocols: Record<string, boolean>
) =>
  routerLabel === SwapRouter.Aftermath
    ? Object.keys(afProtocols).filter(p => !afProtocols[p])
    : Object.keys(cetusProtocols).filter(p => !cetusProtocols[p])

const loadProtocolsFromLS = (key: string, fallback: Record<string, boolean>): Record<string, boolean> => {
  const json = localStorage.getItem(key)
  try {
    return json ? JSON.parse(json) : fallback
  } catch {
    return fallback
  }
}

export const useWithdrawDetails = () => {
  const [slippagePct, setSlippagePct] = useState(INITIAL_SLIPPAGE)
  const [overestimationPct, setOverestimationPct] = useState(INITIAL_OVERESTIMATION)
  const [swapType, setSwapType] = useState<SwapType>(INITIAL_SWAP_TYPE)
  const [afProtocols, setAfProtocols] = useState<Record<string, boolean>>(() =>
    loadProtocolsFromLS(AF_PROTOCOL_LIST_LS_KEY, Object.fromEntries(AfRouterAdapter.protocolList().map(p => [p, true])))
  )
  const [cetusProtocols, setCetusProtocols] = useState<Record<string, boolean>>(() =>
    loadProtocolsFromLS(
      CETUS_PROTOCOL_LIST_LS_KEY,
      Object.fromEntries(CetusAggregatorAdapter.protocolList().map(p => [p, true]))
    )
  )

  const handleAfProtocolUpdate = useCallback((protocols: Record<string, boolean>) => {
    setAfProtocols({ ...protocols })
    localStorage.setItem(AF_PROTOCOL_LIST_LS_KEY, JSON.stringify(protocols))
  }, [])

  const handleCetusProtocolUpdate = useCallback((protocols: Record<string, boolean>) => {
    setCetusProtocols({ ...protocols })
    localStorage.setItem(CETUS_PROTOCOL_LIST_LS_KEY, JSON.stringify(protocols))
  }, [])

  const handleSwapTypeChange = useCallback((newSwapType: SwapType) => setSwapType(newSwapType), [])

  const handleSlippagePctChange = useCallback((value: number) => setSlippagePct(value), [])

  const handleOverestimationPctChange = useCallback((value: number) => setOverestimationPct(value), [])

  const handleResetValues = useCallback(() => {
    setSlippagePct(INITIAL_SLIPPAGE)
    setOverestimationPct(INITIAL_OVERESTIMATION)
    setSwapType(INITIAL_SWAP_TYPE)
    setAfProtocols(Object.fromEntries(AfRouterAdapter.protocolList().map(p => [p, true])))
    setCetusProtocols(Object.fromEntries(CetusAggregatorAdapter.protocolList().map(p => [p, true])))
    localStorage.removeItem(AF_PROTOCOL_LIST_LS_KEY)
    localStorage.removeItem(CETUS_PROTOCOL_LIST_LS_KEY)
  }, [])

  return {
    slippagePct,
    handleSlippagePctChange,
    overestimationPct,
    handleOverestimationPctChange,
    swapType,
    handleSwapTypeChange,
    afProtocols,
    handleAfProtocolUpdate,
    cetusProtocols,
    handleCetusProtocolUpdate,
    handleResetValues,
  }
}
